import React, {useMemo, useState} from 'react';
import '../assets/css/result.css';
import BannerCombined from '../components/BannerCombined';
import Dialog from '../components/Dialog';
import Div from '../components/Div.styled';
import DescriptionPage from '../components/HeadlineSubHeadlineText';
import SpaceLike from '../components/Space';
// eslint-disable-next-line import/no-extraneous-dependencies
import {Swiper, SwiperSlide} from 'swiper/react';
import BannerSlider from '../components/BannerSlider';
import {ButtonCTAStyled} from '../components/CTAStyled';
import useMediaQuery from '../hooks/useMediaQuery';
import {appendQueryParams, renderImage} from '../utils';
import {FeatureCard} from './ResultFeatureCards';
import {IFRAME_SRC} from './registerYourInterest/constants';

interface Feature {
  info?: string;
  title_line_1: string;
  title_line_2: string;
  disclaimer?: string;
}

type SectionOneBannerProps = {
  hero_title?: string;
  hero_image_background?: string;
  hero_image_car?: string;
  hero_image_car_model?: string;
  body_title_line_1?: string;
  body_title_line_2?: string;
  body?: string;
  body_combined?: string;
  name?: string;
  features?: Feature[];
};

type SectionOneCombinedProps = {
  hero_title?: string;
  hero_image_background?: string;
  assetUrl?: string;
  cars?: SectionOneBannerProps[];
  name?: string;
  handleRegisterYourInterest: (model?: string) => void;
  carModel: string;
};

function ResultSectionOneCombined({
  assetUrl,
  cars,
  name,
  carModel,
  handleRegisterYourInterest,
}: SectionOneCombinedProps) {
  const [carLeftData, carRightData] = cars ?? [];
  const [openModal, setOpenModal] = useState(false);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const carLeft = useMemo(() => carLeftData, [carLeftData]);
  const carRight = useMemo(() => carRightData, [carRightData]);
  if (!carLeft || !carRight) throw new Error('incorrect data');
  const [contentSectionOne, setContentSectionOne] = useState(carLeft);
  const {isDesktop, isMobile} = useMediaQuery();
  const pagination = {
    clickable: true,
  };
  const handleChangeSlide = (e: {realIndex: number}) => {
    if (e.realIndex == 1) setContentSectionOne(carRight);
    else setContentSectionOne(carLeft);
  };
  const renderBanner = (bannerProps: SectionOneBannerProps) =>
    assetUrl ? (
      <BannerSlider
        modelText={renderImage(
          assetUrl,
          bannerProps.hero_image_car_model ?? '',
        )}
        carImage={renderImage(assetUrl, bannerProps.hero_image_car ?? '')}
        backgroundImage={renderImage(
          assetUrl,
          bannerProps.hero_image_background ?? '',
        )}
        title={name + ', meet the'}
        titleStrong={bannerProps.hero_title ?? ''}
        height={isMobile ? '540px' : '680px'}
      />
    ) : null;
  const renderFeatureItem = (featureCard: Feature, i: number) => {
    return (
      <FeatureCard
        key={i}
        cardItem={featureCard}
        onClickInfoCard={() => {
          setContent(featureCard?.info || '');
          setTitle(featureCard?.title_line_1 || '');
          setOpenModal(true);
        }}
      />
    );
  };

  const renderCTALabel = (direction: 'left' | 'right') => {
    switch (carModel) {
      case 'id4gtxAndid5gtx':
        return direction === 'left'
          ? 'Explore the ID. 4 GTX'
          : 'Book a test drive';
      case 'id4Andid5':
        return direction === 'left'
          ? 'Book a test drive'
          : 'Explore the ID. 5 Pro';

      default:
        return 'Get in touch';
    }
  };

  const handleCTAClick = (model: string) => {
    switch (model) {
      case 'id4':
        window.open(
          appendQueryParams(IFRAME_SRC, {
            SalesGroupName: 'ID.4-pro',
          }),
          '_blank',
        );
        return;
      case 'id5gtx':
        window.open(
          appendQueryParams(IFRAME_SRC, {
            SalesGroupName: 'ID.5-gtx',
          }),
          '_blank',
        );
        return;
      case 'idBuzz5':
      case 'idBuzz7':
        window.open(
          appendQueryParams(IFRAME_SRC, {
            SalesGroupName: 'ID.buzz',
          }),
          '_blank',
        );
        return;
      case 'idBuzzCargo':
        window.open(
          appendQueryParams(IFRAME_SRC, {
            SalesGroupName: 'ID.buzz-cargo',
          }),
          '_blank',
        );
        return;
      case 'id4gtx':
        window.open(
          'https://www.volkswagen.com.au/en/models/id4-gtx.html',
          '_blank',
        );
        return;
      case 'id5':
        window.open(
          'https://www.volkswagen.com.au/en/models/id5.html',
          '_blank',
        );
        return;

      default:
        break;
    }

    handleRegisterYourInterest(model);
  };

  return (
    <Div>
      {isDesktop ? (
        <>
          {carLeft && carRight && assetUrl && (
            <BannerCombined
              modelTextLeft={assetUrl + carLeft.hero_image_car_model}
              carImageLeft={assetUrl + carLeft.hero_image_car ?? ''}
              modelTextRight={assetUrl + carRight.hero_image_car_model ?? ''}
              carImageRight={assetUrl + carRight.hero_image_car ?? ''}
              backgroundImage={assetUrl + carLeft.hero_image_background ?? ''}
              title={name + ', meet the'}
              titleStrong={carLeft.hero_title + ' & ' + carRight.hero_title}
            />
          )}
          <SpaceLike
            width={'100%'}
            customStyles={{
              justifyContent: 'space-evenly',
              maxWidth: '1400px',
              margin: '50px auto 0',
            }}
            size={40}
          >
            <Div>
              {carLeft && name && (
                <DescriptionPage
                  title={carLeft.body_title_line_1 ?? ''}
                  titleStrong={carLeft.body_title_line_2 ?? ''}
                  descriptions={carLeft.body_combined?.replace(
                    '##name##',
                    name,
                  )}
                />
              )}
              <Div className="section-one-scroll no-scroll">
                <SpaceLike direction="horizontal" size={16} width={'100%'}>
                  {carLeft?.features &&
                    [carLeft?.features[1], carLeft?.features[3]].map(
                      renderFeatureItem,
                    )}
                </SpaceLike>
              </Div>
              <Div padding="0 32px" className="flex-center">
                <ButtonCTAStyled
                  tag="button"
                  emphasis="primary"
                  onClick={() => handleCTAClick(carModel.split('And')[0])}
                  ariaLabel="aria label for CTA"
                  size="large"
                >
                  {renderCTALabel('left')}
                </ButtonCTAStyled>
              </Div>
            </Div>
            <Div
              customStyles={{
                width: 1,
                height: 'auto',
                backgroundColor: '#DFE4E8',
              }}
            />
            <Div>
              {carRight && name && (
                <DescriptionPage
                  title={carRight.body_title_line_1 ?? ''}
                  titleStrong={carRight.body_title_line_2 ?? ''}
                  descriptions={carRight.body_combined?.replace(
                    '##name##',
                    name,
                  )}
                />
              )}
              <Div className="section-one-scroll no-scroll">
                <SpaceLike direction="horizontal" size={16} width={'100%'}>
                  {carRight?.features &&
                    [carRight?.features[1], carRight?.features[3]].map(
                      renderFeatureItem,
                    )}
                </SpaceLike>
              </Div>
              <Div padding="0 32px" className="flex-center">
                <ButtonCTAStyled
                  tag="button"
                  emphasis="primary"
                  onClick={() => handleCTAClick(carModel.split('And')[1])}
                  ariaLabel="aria label for CTA"
                  size="large"
                >
                  {renderCTALabel('right')}
                </ButtonCTAStyled>
              </Div>
            </Div>
          </SpaceLike>
        </>
      ) : (
        <Div width="100%">
          <Div customStyles={{height: isMobile ? '660px' : '830px'}}>
            <Swiper
              spaceBetween={100}
              slidesPerView={1}
              pagination={pagination}
              onSlideChange={handleChangeSlide}
              direction="horizontal"
              style={
                {
                  'width': '100%',
                  'height': '100%',
                  '--swiper-pagination-bullet-width': '47px',
                  '--swiper-pagination-bullet-height': '1px',
                } as React.CSSProperties
              }
            >
              {[carLeft, carRight].map((item, index) => (
                <SwiperSlide key={index} style={{width: '90%', paddingTop: 0}}>
                  {item && renderBanner(item)}
                </SwiperSlide>
              ))}
            </Swiper>
          </Div>
          <Div
            maxWidth={'1116px'}
            margin="0 auto "
            customStyles={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            {contentSectionOne && name && (
              <DescriptionPage
                title={contentSectionOne.body_title_line_1 ?? ''}
                titleStrong={contentSectionOne.body_title_line_2 ?? ''}
                descriptions={contentSectionOne.body_combined?.replace(
                  '##name##',
                  name,
                )}
                CTAButtonPrimaryTitle={renderCTALabel(
                  contentSectionOne.name == carLeft.name ? 'left' : 'right',
                )}
                handleCTAPrimary={() =>
                  handleCTAClick(
                    contentSectionOne.name == carLeft.name
                      ? carModel.split('And')[0]
                      : carModel.split('And')[1],
                  )
                }
              />
            )}
            <Div className="section-one-scroll no-scroll">
              <SpaceLike direction="horizontal" size={16} width={'1116px'}>
                {contentSectionOne.features?.map(renderFeatureItem)}
              </SpaceLike>
            </Div>
          </Div>
        </Div>
      )}
      <Dialog
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        content={content}
        title={title}
      />
    </Div>
  );
}

export default ResultSectionOneCombined;
